import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, Check, ShieldCheck, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';

function RequirementRow({ met, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: met ? '#2e7d32' : '#9aa39c', fontWeight: 600, transition: 'color 0.18s' }}>
      <span style={{
        width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: met ? '#2e7d32' : '#e2e6e2', color: '#fff', transition: 'background 0.18s',
      }}>
        <Check size={10} strokeWidth={3.2} />
      </span>
      {label}
    </div>
  );
}

function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const lengthOk = newPassword.length >= 8;
  const matchOk = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token || !email) { setError(t('authPages.missingResetInfo')); return; }
    if (newPassword.length < 8) { setError(t('authPages.passwordTooShort')); return; }
    if (newPassword !== confirmPassword) { setError(t('authPages.passwordsDontMatch')); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setDone(true);
        setTimeout(() => navigate('/registration'), 2500);
      } else {
        setError(data.error || t('authPages.failedResetPassword'));
      }
    } catch {
      setError(t('authPages.failedResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '11px 42px 11px 38px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' };
  const focusBorder = (e) => { e.target.style.borderColor = '#32614d'; };
  const blurBorder = (e) => { e.target.style.borderColor = '#ddd'; };

  return (
    <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f0', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 16, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <Link to="/registration" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#32614d', fontWeight: 600, fontSize: 13, textDecoration: 'none', marginBottom: 20 }}>
          <ArrowLeft size={15} /> {t('authPages.backToLogin')}
        </Link>

        {done ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#e8f5e9,#d4ecd9)', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 14px rgba(50,97,77,0.18)' }}>
              <Check size={26} strokeWidth={2.6} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 8px' }}>{t('authPages.passwordUpdated')}</h2>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>{t('authPages.redirecting')}</p>
          </div>
        ) : (
          <>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'linear-gradient(135deg,#32614d,#2a5240)', color: '#e8f5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, boxShadow: '0 6px 16px rgba(42,82,64,0.28)',
            }}>
              <ShieldCheck size={22} strokeWidth={2.2} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a3d2e', margin: '0 0 6px' }}>{t('authPages.chooseNewPassword')}</h2>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 24px' }}>{email ? t('authPages.forAccount', { email }) : t('authPages.yourAccount')}</p>
            <form onSubmit={handleSubmit}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>{t('authPages.newPassword')}</label>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <Lock size={16} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('authPages.atLeast8Chars')} required autoFocus
                  style={inputStyle} onFocus={focusBorder} onBlur={blurBorder}
                />
                <button type="button" onClick={() => setShowNew((v) => !v)} aria-label={t('authPages.togglePasswordVisibility')}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#999', display: 'flex' }}>
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>{t('authPages.confirmNewPassword')}</label>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <Lock size={16} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('authPages.reenterPassword')} required
                  style={inputStyle} onFocus={focusBorder} onBlur={blurBorder}
                />
                <button type="button" onClick={() => setShowConfirm((v) => !v)} aria-label={t('authPages.togglePasswordVisibility')}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#999', display: 'flex' }}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 16 }}>
                <RequirementRow met={lengthOk} label={t('authPages.passwordReqLength')} />
                <RequirementRow met={matchOk} label={t('authPages.passwordReqMatch')} />
              </div>

              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 10, fontSize: 12, border: '1px solid #ffcdd2', marginBottom: 14 }}>
                  <AlertCircle size={14} style={{ flexShrink: 0 }} /> {localizeMessage(error)}
                </div>
              )}
              <button type="submit" disabled={loading}
                style={{ width: '100%', padding: 13, borderRadius: 10, background: loading ? '#ccc' : 'linear-gradient(135deg,#32614d,#2a5240)', color: 'white', border: 'none', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? t('authPages.updating') : t('authPages.updatePassword')}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default ResetPassword;
